import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { Check, Zap, CreditCard, Users, ShieldCheck } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";

const SYMBOL = { eur: "€", gbp: "£", usd: "$" };

export function guessCurrency() {
  try {
    const saved = localStorage.getItem("octon_currency");
    if (saved && SYMBOL[saved]) return saved;
  } catch (_) {}
  const lang = (navigator.language || "").toUpperCase();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (lang.endsWith("-GB") || tz === "Europe/London") return "gbp";
  if (lang.endsWith("-US") || tz.startsWith("America/")) return "usd";
  return "eur";
}

export function CurrencyPicker({ value, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-[#343A4A] p-1" role="radiogroup" aria-label="Currency">
      {Object.keys(SYMBOL).map((c) => (
        <button key={c} type="button" role="radio" aria-checked={value === c} data-testid={`currency-${c}`}
          onClick={() => { onChange(c); try { localStorage.setItem("octon_currency", c); } catch (_) {} }}
          className={`px-4 h-8 rounded-full text-sm font-medium transition-colors ${
            value === c ? "bg-acid text-black" : "text-[#8A91A6] hover:text-white"}`}>
          {SYMBOL[c]} {c.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Membership() {
  const { user, setUser } = useAuth();
  const [currency, setCurrency] = useState(guessCurrency);
  const [accepted, setAccepted] = useState(false);
  const [mParams] = useSearchParams();
  const wanted = mParams.get("plan");
  const [busy, setBusy] = useState("");
  const { data: plans = [] } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/api/membership/plans")).data });
  const { data: cfg } = useQuery({ queryKey: ["membership-config"], queryFn: async () => (await api.get("/api/membership/config")).data });

  const checkout = async (lookup_key) => {
    if (!accepted) { toast.error("Please tick the box to accept the Terms of Service first."); return; }
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/api/payments/checkout", {
        lookup_key, currency, accepted_terms: true, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiErr(err)); setBusy(""); }
  };

  const portal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/api/payments/portal");
      window.location.href = data.url;
    } catch (err) { toast.error(apiErr(err)); setBusy(""); }
  };

  // Re-check billing with Stripe when the page opens (e.g. after returning from Manage billing)
  useEffect(() => {
    if (!user?.has_billing) return;
    api.post("/api/payments/refresh").then(({ data }) => setUser && setUser(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = user?.membership_status === "active";
  const endsOn = user?.membership_ends_at ? new Date(user.membership_ends_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : null;
  const currentName = plans.find((p) => p.lookup_key === user?.membership_plan)?.name || "current";
  const subtitle =
    user?.membership_status === "past_due" ? "Your last payment didn't go through. Update your card under Manage billing to keep full access."
    : active && user?.membership_cancel_at_period_end ? `You're on the ${currentName} plan until ${endsOn || "the end of this billing month"}. It won't renew.`
    : active ? `You're on the ${currentName} plan. Change or cancel any time under Manage billing.`
    : user?.club_active && user?.club_role === "athlete" ? `You're training with ${user.club_name}. Your membership is covered by your club.`
    : user?.membership_status === "trialing" ? "You're on your free 7-day taster with full access to OCTON. Pick a plan below to keep training after it ends."
    : "Pick a plan to keep training with your OCTON coach.";

  return (
    <div className="p-4 md:p-8 space-y-8">
      <PageBanner image={IMG.membership} overline="membership" title="Choose your plan" subtitle={subtitle} />

      <JoinClub user={user} />

      {cfg?.test_mode && (
        <p className="text-center text-xs text-[#8A91A6]" data-testid="test-mode-note">
          Test mode: use card 4242 4242 4242 4242, any future date, any CVC. No real money is taken.
        </p>
      )}

      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <CurrencyPicker value={currency} onChange={setCurrency} />
        {user?.has_billing && (
          <Button onClick={portal} disabled={busy === "portal"} variant="outline" data-testid="manage-billing"
            className="rounded-full h-10 px-5 border-[#343A4A] bg-transparent hover:bg-white/5">
            <CreditCard className="w-4 h-4" /> Manage billing
          </Button>
        )}
      </div>

      {!(user?.has_billing && active) && (
        <label className="flex items-start gap-3 max-w-2xl mx-auto text-sm surface rounded-xl p-4 text-[#B7BECB] cursor-pointer" data-testid="terms-accept">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#CCFF00]" data-testid="terms-checkbox" />
          <span>
            I accept the <Link to="/terms" target="_blank" className="text-acid underline">Terms of Service</Link> and
            have read the <Link to="/privacy" target="_blank" className="text-acid underline">Privacy Policy</Link>.
            I understand my membership renews monthly until I cancel, and I ask for it to start straight away
            (see the <Link to="/refunds" target="_blank" className="text-acid underline">14-day withdrawal and refund policy</Link>).
          </span>
        </label>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
        {plans.map((p, i) => {
          const current = user?.membership_plan === p.lookup_key && active;
          const amount = p.prices?.[currency] ?? p.price;
          return (
            <motion.div key={p.lookup_key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className={`relative rounded-xl p-7 flex flex-col ${p.popular ? "tracing-beam" : "surface"} ${wanted === p.lookup_key ? "ring-2 ring-acid" : ""}`} data-testid={`plan-${p.lookup_key}`}>
              {p.popular && <div className="tracing-beam-inner rounded-xl" />}
              <div className="relative flex flex-col flex-1">
                {p.popular && (
                  <span className="absolute -top-3 right-0 bg-acid text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Zap className="w-3 h-3" /> POPULAR
                  </span>
                )}
                <h3 className="font-heading font-black text-2xl tracking-tight">{p.name}</h3>
                <p className="text-[#8A91A6] text-sm mt-1 h-10">{p.tagline}</p>
                <div className="my-5">
                  <span className="font-heading font-black text-4xl" data-testid={`price-${p.lookup_key}`}>{SYMBOL[currency]}{amount}</span>
                  <span className="text-[#8A91A6] text-sm">/month</span>
                  <p className="text-[#565C6F] text-xs mt-1">incl. VAT where applicable</p>
                </div>
                <ul className="space-y-2.5 mb-7 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-white/85">
                      <Check className="w-4 h-4 text-acid mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => (user?.has_billing && active ? portal() : checkout(p.lookup_key))}
                  disabled={current || !!busy} data-testid={`checkout-${p.lookup_key}`}
                  className={`w-full h-11 rounded-md font-medium ${
                    p.popular ? "bg-acid text-black hover:bg-[#AACC00]" : "bg-white text-black hover:bg-acid"
                  }`}>
                  {current ? "Current plan" : busy === p.lookup_key ? "Opening checkout…" : user?.has_billing && active ? "Switch plan" : "Choose " + p.name}
                </Button>
              </div>
            </motion.div>
          );
        })}
      </div>

    </div>
  );
}


export function JoinClub({ user }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const stored = (() => { try { return localStorage.getItem("octon_club_code") || ""; } catch (_) { return ""; } })();
  const [code, setCode] = useState(params.get("club") || stored);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  if (user?.club_role === "owner") {
    return (
      <div className="max-w-4xl mx-auto rounded-xl border border-acid/40 bg-acid/5 p-4 flex flex-wrap items-center gap-3">
        <Users className="w-5 h-5 text-acid" />
        <p className="flex-1 text-sm">You run <b>{user.club_name}</b>. Invite athletes and see your squad's progress in My Club.</p>
        <Button onClick={() => navigate("/app/club")} className="bg-acid text-black hover:bg-[#AACC00] rounded-full">Open My Club</Button>
      </div>
    );
  }
  if (user?.club_id) {
    return (
      <div className="max-w-4xl mx-auto rounded-xl border border-[#1E222A] bg-[#0F1115] p-4 flex flex-wrap items-center gap-3">
        <Users className="w-5 h-5 text-acid" />
        <p className="flex-1 text-sm">You're a member of <b>{user.club_name}</b>{user.club_active ? "." : ", but the club's plan isn't active right now."}</p>
      </div>
    );
  }
  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/club/join", { code, accept_sharing: agree });
      try { localStorage.removeItem("octon_club_code"); } catch (_) {}
      toast.success(`Welcome to ${data.club.name}!`);
      if (data.has_own_plan) toast("Your club now covers your membership. You can cancel your own plan under Manage billing.");
      await refresh(); qc.invalidateQueries();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-4xl mx-auto rounded-xl border border-[#1E222A] bg-[#0F1115] p-5" data-testid="join-club">
      <p className="font-heading font-bold flex items-center gap-2"><Users className="w-4 h-4 text-acid" /> Training with a club?</p>
      <p className="text-sm text-[#8A91A6] mt-1">Enter the code from your coach. Your club's plan covers your membership.</p>
      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CLUB CODE" maxLength={8}
          className="h-11 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 font-mono2 tracking-[0.3em] sm:w-48" data-testid="club-code" />
        <Button onClick={join} disabled={busy || code.length < 6 || !agree} className="h-11 bg-acid text-black hover:bg-[#AACC00] rounded-lg" data-testid="club-join-btn">
          {busy ? "Joining…" : "Join club"}
        </Button>
      </div>
      <label className="flex items-start gap-2 mt-3 text-xs text-[#B7BECB] cursor-pointer">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#CCFF00]" />
        <span><ShieldCheck className="inline w-3.5 h-3.5 text-acid mr-1" />I agree that my club's coaches can see my training activity (sessions, lifts, PRs and consistency). They can't see my coach chats, wellbeing check-ins or payment details.</span>
      </label>
    </div>
  );
}
